import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, Award, HeartHandshake, Calendar, ShoppingBag, ShieldCheck, Megaphone, Flame, X, PartyPopper } from 'lucide-react';

const API = '';

const BADGE_ICONS = {
  star: Star,
  award: Award,
  'heart-handshake': HeartHandshake,
  calendar: Calendar,
  'shopping-bag': ShoppingBag,
  'shield-check': ShieldCheck,
  megaphone: Megaphone,
  flame: Flame,
};

const BadgeEarnedPopup = () => {
  const [newBadges, setNewBadges] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  const checkNewBadges = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/badges/new`);
      if (res.data.new_badges?.length > 0) {
        setNewBadges(res.data.new_badges);
        setCurrentIndex(0);
        setVisible(true);
      }
    } catch {
      // Silently fail
    }
  }, []);

  // Poll for new badges every 60 seconds
  useEffect(() => {
    // Initial check after 5 seconds
    const initialTimer = setTimeout(checkNewBadges, 5000);

    // Then every 60 seconds
    const interval = setInterval(checkNewBadges, 60000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [checkNewBadges]);

  const dismiss = () => {
    if (currentIndex < newBadges.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setVisible(false);
      setNewBadges([]);
    }
  };

  if (!visible || newBadges.length === 0) return null;

  const badge = newBadges[currentIndex];
  const Icon = BADGE_ICONS[badge.icon] || Star;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="badge-earned-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={dismiss} />

      {/* Card */}
      <div className="relative bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Confetti-style top bar */}
        <div
          className="h-2 w-full"
          style={{ background: `linear-gradient(90deg, ${badge.color}, #D4A017, ${badge.color})` }}
        />

        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          data-testid="badge-popup-close"
        >
          <X className="h-4 w-4 text-slate-400" />
        </button>

        <div className="px-8 pt-8 pb-6 text-center">
          {/* Celebration icon */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <PartyPopper className="h-5 w-5 text-[#D4A017] animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="text-xs font-bold uppercase tracking-widest text-[#D4A017]">Badge Earned!</span>
            <PartyPopper className="h-5 w-5 text-[#D4A017] animate-bounce" style={{ animationDelay: '200ms' }} />
          </div>

          {/* Badge icon */}
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg"
            style={{ background: `${badge.color}15`, border: `3px solid ${badge.color}` }}
          >
            <Icon className="h-10 w-10" style={{ color: badge.color }} />
          </div>

          {/* Badge info */}
          <h2 className="text-xl font-bold text-[#0F2C3E] dark:text-white mb-2">{badge.name}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{badge.description}</p>

          {/* CTA */}
          <button
            onClick={dismiss}
            className="w-full py-3 rounded-xl font-bold text-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: badge.color, color: 'white' }}
            data-testid="badge-popup-cta"
          >
            {currentIndex < newBadges.length - 1 ? 'Next Badge' : 'Awesome!'}
          </button>

          {newBadges.length > 1 && (
            <p className="text-xs text-slate-400 mt-3">
              {currentIndex + 1} of {newBadges.length} new badges
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BadgeEarnedPopup;
